
import { useState, useEffect, Suspense } from "react";
import { Clapperboard, ArrowRight } from "lucide-react";
import { ContentSection } from "./SectionContainers";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { DerivativeCreation } from "@/data/models/types";
import { Skeleton } from "@/components/ui/skeleton";
import { UnifiedDerivativeDisplay } from "@/components/derivatives/UnifiedDerivativeDisplay";

interface FeaturedDerivativesProps {
  derivatives: DerivativeCreation[];
}

export const FeaturedDerivatives = ({ derivatives }: FeaturedDerivativesProps) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  
  // Filter derivatives without video to avoid display errors
  const validDerivatives = derivatives.filter(d => d.videoUrl || d.thumbnail);
  
  // Enhanced debugging for social metrics - DIRECT ACCESS APPROACH
  console.log("FeaturedDerivatives - received derivatives:", derivatives.length);
  if (derivatives.length > 0) {
    const firstItem = derivatives[0];
    console.log("FeaturedDerivatives - first item FOCUSED VIEW COUNT DEBUG:", {
      id: firstItem.id,
      title: firstItem.title,
      views: typeof firstItem.views === 'number' ? firstItem.views : 'undefined/invalid',
      likesCount: typeof firstItem.likesCount === 'number' ? firstItem.likesCount : 'undefined/invalid', 
      favoritesCount: typeof firstItem.favoritesCount === 'number' ? firstItem.favoritesCount : 'undefined/invalid',
      commentsCount: typeof firstItem.commentsCount === 'number' ? firstItem.commentsCount : 'undefined/invalid'
    });
    
    // Log all derivatives views for debugging with full type info
    console.log("All derivatives with view counts:", derivatives.map(d => ({ 
      id: d.id, 
      title: d.title?.substring(0, 20) || 'No title', 
      views: typeof d.views === 'number' ? d.views : `Invalid type: ${typeof d.views}`
    })));
  } else {
    console.log("FeaturedDerivatives - No derivatives received");
  }
  
  useEffect(() => {
    // Use Intersection Observer to detect when the component enters viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    // Get current reference to the container
    const sectionRef = document.getElementById('featured-derivatives-section');
    if (sectionRef) {
      observer.observe(sectionRef);
    }
    
    return () => {
      if (sectionRef) {
        observer.unobserve(sectionRef);
      }
    };
  }, [derivatives]);
  
  if (validDerivatives.length === 0) {
    return null;
  }
  
  const sectionTitle = typeof t('explore.derivatives.title') === 'string' 
    ? t('explore.derivatives.title') 
    : 'Latest Creations';
    
  const sectionDescription = typeof t('explore.derivatives.description') === 'string'
    ? t('explore.derivatives.description')
    : 'Explore creative video remixes';
  
  const viewAllButton = (
    <Button variant="outline" size="sm" asChild className="whitespace-nowrap">
      <Link to="/derivatives" className="flex items-center">
        {typeof t("common.viewAll") === 'string' ? t("common.viewAll") : "View All"} 
        <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    </Button>
  );
  
  // Check one more time to ensure the data we're passing to the component is valid
  console.log("FINAL CHECK BEFORE RENDERING: First derivative in validDerivatives:", 
    validDerivatives.length > 0 ? 
    {
      id: validDerivatives[0].id,
      views: validDerivatives[0].views,
      title: validDerivatives[0].title
    } : 'No valid derivatives'
  );
  
  return (
    <div id="featured-derivatives-section" className="py-12">
      <ContentSection 
        title={sectionTitle}
        description={sectionDescription}
        icon={Clapperboard}
        viewAllText={viewAllButton}
      >
        {!isVisible ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="aspect-[9/16] rounded-lg" />
            ))}
          </div>
        ) : (
          <Suspense fallback={
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} className="aspect-[9/16] rounded-lg" />
              ))}
            </div>
          }>
            <UnifiedDerivativeDisplay
              items={validDerivatives}
              displayMode="carousel"
              forceVerticalLayout={true}
              initialItemsToShow={8}
              columns={{ sm: 1, md: 2, lg: 3, xl: 4 }}
            />
          </Suspense>
        )}
      </ContentSection>
    </div>
  );
};

export default FeaturedDerivatives;
